// @oasis-permission-inherits: /videos (mirage-videos:view)
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useAuthStore } from "~/modules/auth/auth-store";
import ResponsiveGrid from "~/modules/mirage/components/responsive-grid";
import { isSegmentReviewEndpointMissing } from "~/modules/segment-review/api-errors";
import { ReferenceThumbnail } from "~/modules/segment-review/components/reference-thumbnail";
import {
  retractSegmentReviewMutation,
  submitSegmentReviewMutation,
} from "~/modules/segment-review/mutations";
import { listSegmentReviewsQuery } from "~/modules/segment-review/queries";
import { ReviewItem } from "~/modules/segment-review/schemas";
import { VideoCard } from "~/modules/videos/components/video-card";

export const Route = createFileRoute("/_dash/videos/segment-eval/results")({
  validateSearch: z.object({
    verdict: z.enum(["rejected", "watched", "all"]).optional().default("rejected"),
  }),
  component: RouteComponent,
});

const FILTER_OPTIONS = [
  { value: "rejected", label: "😱 Rejected" },
  { value: "watched", label: "Watched" },
  { value: "all", label: "All" },
] as const;

function RouteComponent() {
  const { verdict } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const isSegmentReviewAdmin = useAuthStore((state) => state.user?.isSegmentReviewAdmin === true);

  const { data, error, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    listSegmentReviewsQuery.useInfiniteQuery(
      {
        params: {
          ...(verdict === "all" ? {} : { verdict }),
        },
      },
      { retry: false }
    );

  const reviews = data?.pages.flatMap((page) => page.reviews.page) ?? [];
  const counts = data?.pages[0]?.counts;

  const [retracted, setRetracted] = useState<Record<string, ReviewItem>>({});

  const serverSegmentIds = new Set(reviews.map((review) => review.segmentId));
  const retainedRetracted = Object.values(retracted).filter(
    (review) => !serverSegmentIds.has(review.segmentId)
  );
  const displayReviews = [...reviews, ...retainedRetracted].sort(
    (a, b) => b.reviewedAt - a.reviewedAt
  );

  const submitReview = submitSegmentReviewMutation.useMutation({
    onSuccess: (result) => {
      setRetracted((prev) => {
        const next = { ...prev };
        delete next[result.review.segmentId];
        return next;
      });
      toast.success(
        result.review.verdict === "rejected" ? "Review updated to rejected" : "Marked as watched"
      );
    },
    onError: () => {
      toast.error("Failed to update review");
    },
  });

  const retractReview = retractSegmentReviewMutation.useMutation({
    onError: () => {
      toast.error("Failed to retract review");
    },
  });

  const handleVerdictAction = (review: ReviewItem, isRetracted: boolean) => {
    if (submitReview.isPending || retractReview.isPending) {
      return;
    }
    if (!isRetracted && review.verdict === "rejected") {
      retractReview.mutate(
        {
          pathParams: { segmentId: review.segmentId },
          params: {},
        },
        {
          onSuccess: () => {
            setRetracted((prev) => ({ ...prev, [review.segmentId]: review }));
            toast.success("Rejection removed — react 😱 to re-reject, or leave it for re-review");
          },
        }
      );
      return;
    }
    submitReview.mutate({
      pathParams: { segmentId: review.segmentId },
      params: { verdict: "rejected" },
    });
  };

  const handleWatchedToEnd = (review: ReviewItem) => {
    if (submitReview.isPending || retractReview.isPending) {
      return;
    }
    submitReview.mutate({
      pathParams: { segmentId: review.segmentId },
      params: { verdict: "watched" },
    });
  };

  const endpointMissing = isSegmentReviewEndpointMissing(error);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold">cooked ™ results</h1>
          {counts && (
            <p className="text-sm text-muted-foreground">
              {counts.watched} watched · {counts.rejected} rejected
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {FILTER_OPTIONS.map((option) => (
            <Button
              key={option.label}
              variant={verdict === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                navigate({ search: { verdict: option.value } });
              }}
            >
              {option.label}
            </Button>
          ))}
          <Button asChild variant="outline" size="sm">
            <Link to="/videos/segment-eval">feed</Link>
          </Button>
          {isSegmentReviewAdmin && (
            <Button asChild variant="outline" size="sm">
              <Link to="/videos/segment-eval/admin">preference training</Link>
            </Button>
          )}
        </div>
      </div>

      {endpointMissing && (
        <div className="rounded-md border border-dashed p-5 text-sm text-muted-foreground">
          Segment review results are not available yet (the captions-server endpoint is pending
          deploy).
        </div>
      )}
      {error && !endpointMissing && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm">
          <p className="font-medium">Failed to load reviews</p>
          <pre className="mt-2 overflow-x-auto text-xs">{error.message}</pre>
        </div>
      )}

      <ResponsiveGrid gap={16} maxColumns={5} itemMinWidth={200} itemMinHeight={200}>
        {isLoading &&
          !endpointMissing &&
          Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} className="aspect-9/16 w-full h-full" />
          ))}
        {displayReviews.map((review) => {
          const isRetracted = !serverSegmentIds.has(review.segmentId);
          const isRejected = !isRetracted && review.verdict === "rejected";
          let actionTitle = "Mark as rejected";
          let actionAriaLabel = "Mark as rejected";
          if (isRetracted) {
            actionTitle = "Re-reject";
            actionAriaLabel = "Re-reject";
          } else if (isRejected) {
            actionTitle = "Un-reject (deletes your review; segment returns to the queue)";
            actionAriaLabel = "Un-reject (delete review)";
          }
          return (
            <div key={review.segmentId} className="relative">
              <div className={isRetracted ? "opacity-60" : ""}>
                <VideoCard
                  video={{
                    id: review.videoId,
                    createdAt: review.reviewedAt,
                    completedAt: review.reviewedAt,
                    initialVideoUrl: review.url,
                    caller: "",
                  }}
                  linkTo="/videos/view/$videoId"
                  linkParams={{ videoId: review.videoId }}
                  showBookmarkButton={false}
                  onEnded={isRetracted ? () => handleWatchedToEnd(review) : undefined}
                />
              </div>
              <ReferenceThumbnail
                url={review.referenceUrl}
                type={review.referenceType}
                promptText={review.promptText}
                collapsedClassName="h-12 max-w-24"
              />
              <Button
                size="icon"
                variant="ghost"
                className={`absolute top-2 right-2 z-10 h-10 w-10 rounded-full text-2xl ${
                  isRejected
                    ? "bg-red-600/80 ring-2 ring-red-400 hover:bg-red-600/80"
                    : "bg-black/50 hover:bg-black/70"
                }`}
                title={actionTitle}
                aria-label={actionAriaLabel}
                aria-pressed={isRejected}
                onClick={() => handleVerdictAction(review, isRetracted)}
              >
                <span>😱</span>
              </Button>
              {!isRetracted && review.verdict === "watched" && (
                <span className="pointer-events-none absolute top-13 right-2 z-10 rounded bg-background/80 px-1.5 py-0.5 text-xs text-foreground">
                  Watched ✓
                </span>
              )}
              {isRetracted && (
                <span className="pointer-events-none absolute top-13 right-2 z-10 rounded bg-background/80 px-1.5 py-0.5 text-xs text-muted-foreground">
                  Unreviewed
                </span>
              )}
            </div>
          );
        })}
        {!isLoading && !error && displayReviews.length === 0 && (
          <div className="flex text-sm text-muted-foreground">No reviews yet</div>
        )}
      </ResponsiveGrid>

      {hasNextPage && !isLoading && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
